// apps/fe/src/modules/catalog/screens/catalog-home-screen.tsx

'use client';
import React, { useEffect, useMemo, useCallback } from 'react';
import { EventBus } from '@/shared/utils';
import { Catalog as CatalogDict } from '@/shared/i18n';
import { CatalogEvents, CatalogEventKeys } from '../events';
import { useFilters } from '../hooks/use-filters';
import { CatalogViewData } from '../types/catalog-view-data';
import BaseCatalogPage from '../ui/pages/base-catalog-page';
import CatalogSearch from '../ui/common/search/catalog-search';
import { SwipableCatalogCarousel } from '../ui/common/carousel/home-carousel';
import { useProfileCatalogData } from '../hooks/use-profile-catalog-data';
import { useAuthStore } from '@/auth';
import LinearProgress from '@mui/material/LinearProgress';

interface CatalogHomeScreenProps {
  dict: CatalogDict;
  eventBus: EventBus<CatalogEvents>;
  initialData: CatalogViewData;
  isLoggedIn: boolean;
  isLoadingNonEssential?: boolean;
}

/**
 * Main catalog home screen component.
 * Shows featured content, search, and filtering options.
 *
 * Wrapped in React.memo to prevent unnecessary re-renders
 */
const CatalogHomeScreen = React.memo(function CatalogHomeScreen({
  dict,
  eventBus,
  initialData,
  isLoggedIn,
  isLoadingNonEssential = false,
}: CatalogHomeScreenProps) {
  const { user } = useAuthStore();

  // Memoize the catalog params to avoid new references on each render
  const catalogParams = useMemo(() => ({}), []);

  // Memoize filter data from SSG to always be available immediately
  const filtersData = useMemo(() => initialData.filters, [initialData.filters]);

  // Rehydrate catalog data with user-specific information if logged in
  const {
    data: enhancedData,
    loading: loadingUserData,
    hasUserData,
    updateFavoriteStatus,
  } = useProfileCatalogData({
    dataType: 'view',
    initialData,
    catalogParams,
    eventBus,
  });

  // Memoize catalog data to avoid re-rendering
  const catalogData = useMemo(() => {
    // For authenticated users with enhanced data, use it
    if (user && hasUserData) {
      return {
        ...enhancedData,
        filters: filtersData,
      };
    }

    // For non-authenticated users, just use initialData directly
    return initialData;
  }, [user, hasUserData, enhancedData, initialData, filtersData]);

  // Set up filters with proper authentication flag for registered users
  const filters = useFilters({
    eventBus,
    filters: catalogData.filters,
    withAuthentication: !!user,
  });

  // Handle favorite changes from filtered view
  const handleFavoriteChange = useCallback(
    (courseId: string, favouriteId: string | undefined) => {
      if (updateFavoriteStatus) {
        updateFavoriteStatus(courseId, favouriteId);
      }
    },
    [updateFavoriteStatus],
  );

  // Emit the view event to signal catalog is ready to be viewed - once on mount
  useEffect(() => {
    eventBus.emit(CatalogEventKeys.catalog.view, undefined);
  }, [eventBus]);

  // Memoize the search and carousel components to avoid recreation on every render
  const topComponents = useMemo(
    () => (
      <>
        <CatalogSearch dict={dict} eventBus={eventBus} />
        <SwipableCatalogCarousel dict={dict} sliderItems={catalogData.sliderItems} />
      </>
    ),
    [dict, eventBus, catalogData.sliderItems, isLoadingNonEssential],
  );

  // Determine if we should show the loading state for catalog sections
  // Include user data loading, non-essential data loading, and filter loading state
  const isLoadingCatalogData =
    loadingUserData || isLoadingNonEssential || filters.loadingCatalogData;

  return (
    <BaseCatalogPage
      dict={dict}
      eventBus={eventBus}
      isLoggedIn={isLoggedIn}
      filteredItems={filters.filteredItems}
      loadingCatalogData={isLoadingCatalogData}
      filteringCatalogData={filters.filteringCatalogData}
      activeFilters={filters.activeFilters}
      parentInstruments={filters.parentInstruments}
      filters={catalogData.filters}
      categories={catalogData.categories}
      renderTopComponent={topComponents}
      lastCourseElementRef={filters.lastCourseElementRef}
      instrumentsFilterOnChange={filters.instrumentsFilterOnChange}
      handleSkillLevelsChange={filters.handleSkillLevelsChange}
      handleGenresChange={filters.handleGenresChange}
      handleTopicsChange={filters.handleTopicsChange}
      handleTypeChange={filters.handleTypeChange}
      onFavoriteChange={handleFavoriteChange}
      isEnrichingData={loadingUserData}
      onResetSidebarFilters={filters.onResetSidebarFilters}
      isAnyFilterActive={filters.isAnyFilterActive}
    />
  );
});

export default CatalogHomeScreen;
